<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { Notify, useQuasar } from 'quasar';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import ReorderableTable from '@/components/ReorderableTable.vue';
import StandardSelect from '@/components/StandardSelect.vue';
import DocumentSubmissionDialog from '@/components/dialogs/DocumentSubmissionDialog.vue';
import { Constants } from '@/constants';
import { getStateLabel } from '@/helpers/documentSubmission.helper';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { getFullName, getMaxLengthText } from '@/helpers/text.helper';
import { formatDate } from '@/plugins/i18n';
import type {
  DocumentSubmissionFilter,
  DocumentSubmissionListItemDto,
  Locales,
  ProjectModuleDto,
} from '@/services/types/api.types';
import { useDocumentSubmissionsStore } from '@/stores/documentSubmissions.store';

const { State } = Constants.ProjectModule.DocumentSubmission;

// CONST
const { t } = useI18n();
const $q = useQuasar();
const route = useRoute();
const router = useRouter();

// STORE
const documentSubmissionsStore = useDocumentSubmissionsStore();
const { documentSubmissions, documentSubmissionsFilter } = storeToRefs(documentSubmissionsStore);

// PROPS
const props = defineProps<{
  module: ProjectModuleDto;
  languageEnabled: Locales[];
}>();

// REFS
const filter = ref<DocumentSubmissionFilter>('toReview');
const pagination = ref({ page: 1, rowsPerPage: Constants.Pagination.DefaultRowsPerPage });

// COMPUTED
const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    field: (row: DocumentSubmissionListItemDto) => getMaxLengthText(row.latestTitle),
    align: 'left',
  },
  {
    name: 'participant',
    label: t('project-module.documentSubmission.participant'),
    field: (row: DocumentSubmissionListItemDto) => getFullName(row.user) || '—',
    align: 'left',
  },
  {
    name: 'version',
    label: t('project-module.documentSubmission.version'),
    field: (row: DocumentSubmissionListItemDto) => `v${row.latestVersionNumber}`,
    align: 'left',
  },
  {
    name: 'state',
    label: t('global.label.status'),
    field: (row: DocumentSubmissionListItemDto) => getStateLabel(row.state),
    align: 'left',
  },
  {
    name: 'updated',
    label: t('project-module.documentSubmission.updated'),
    field: (row: DocumentSubmissionListItemDto) => formatDate(row.updated_at),
    align: 'left',
  },
]);

const filterOptions = computed(() =>
  (
    [
      'toReview',
      'onSite',
      State.Pending,
      State.Published,
      State.PublishedWithPendingVersion,
      State.NotPublished,
      State.Withdrawn,
      'all',
    ] as DocumentSubmissionFilter[]
  ).map((value) => ({ label: getStateLabel(value), value })),
);

// The project page's order is set here, one swap with a neighbour at a time, so this view is never paged: a document
// on the first row of a page could not move past the last one of the page before.
const isOnSite = computed(() => filter.value === 'onSite');

// The arrows wait for the On the site rows: the rows of the filter before are not neighbours on the project page.
const canReorder = computed(() => isOnSite.value && documentSubmissionsFilter.value === 'onSite');

// rowsPerPage 0 is q-table's "all".
const shownPage = computed(() => (isOnSite.value ? { page: 1, rowsPerPage: 0 } : pagination.value));

const rows = computed(() => {
  const { page, rowsPerPage } = shownPage.value;
  if (!rowsPerPage) return documentSubmissions.value;

  const start = (page - 1) * rowsPerPage;
  return documentSubmissions.value.slice(start, start + rowsPerPage);
});

const paginationModel = computed(() => ({ ...shownPage.value, rowsNumber: documentSubmissions.value.length }));

const loading = computed(() => !!props.module.id && documentSubmissionsStore.isItemLoading(props.module.id));

// FUNCTIONS
const fetchList = async () => {
  if (!props.module.id) return;
  const loaded = await documentSubmissionsStore.fetchDocumentSubmissionsByModuleId(props.module.id, filter.value);
  if (!loaded) {
    Notify.create({
      color: 'negative',
      message: t('project-module.documentSubmission.listLoadError'),
      position: 'top',
      timeout: 2000,
    });
  }
};

/** Opens a document's review dialog, and reloads the list once it closes: a decision may have moved the row. */
const openDocument = (documentSubmissionId: number) => {
  $q.dialog({
    component: DocumentSubmissionDialog,
    componentProps: { documentSubmissionId, languageEnabled: props.languageEnabled },
  }).onDismiss(fetchList);
};

const handleRowClick = (row: DocumentSubmissionListItemDto) => openDocument(row.id);

/** Moves a document past its neighbour on the project page. The server swaps the positions; the list then reloads. */
const handleReorder = async (source: DocumentSubmissionListItemDto, target: DocumentSubmissionListItemDto) => {
  await wrapSavingOperation(() => documentSubmissionsStore.swapDocumentSubmissionOrder(source.id, target.id));
  await fetchList();
};

const handleRequest = (requested: { page: number; rowsPerPage: number }) => {
  pagination.value = { page: requested.page, rowsPerPage: requested.rowsPerPage };
};

// WATCH
// The filter runs on the server.
watch(filter, async () => {
  pagination.value = { ...pagination.value, page: 1 };
  await fetchList();
});

// A reload can leave fewer rows than the current page starts at.
watch(
  () => documentSubmissions.value.length,
  (length) => {
    const { page, rowsPerPage } = pagination.value;
    const lastPage = rowsPerPage ? Math.max(1, Math.ceil(length / rowsPerPage)) : 1;
    if (page > lastPage) pagination.value = { ...pagination.value, page: lastPage };
  },
);

// LIFECYCLE
onMounted(async () => {
  await fetchList();

  // The review link of the admin notification: `?documentSubmissionId=<id>#documentSubmissions`.
  const linkedId = Number(route.query.documentSubmissionId);
  if (Number.isInteger(linkedId) && linkedId > 0) {
    // Opened once: a reload of the page should not open it again. The link leaves the URL before the dialog
    // opens, since a QDialog closes on any route change.
    const { documentSubmissionId: _linked, ...query } = route.query;
    await router.replace({ query, hash: route.hash });
    openDocument(linkedId);
  }
});
</script>

<template>
  <div class="document-submission-list">
    <reorderable-table
      :rows="rows"
      :columns="columns"
      :title="t('project-module.documentSubmission.title')"
      itemKey="id"
      :loading="loading"
      :disable-add-button="true"
      :disable-orderable="!canReorder"
      :disable-delete-button="true"
      :pagination-enabled="!isOnSite"
      :pagination="paginationModel"
      @row-click="handleRowClick"
      @request="handleRequest"
      @reorder="handleReorder"
    >
      <template v-slot:filters>
        <standard-select
          name="filter"
          :label="t('project-module.documentSubmission.filter')"
          class="filters"
          v-model="filter"
          :options="filterOptions"
          emit-value
          map-options
          outlined
          hide-bottom-space
        />
      </template>
    </reorderable-table>
  </div>
</template>

<style lang="scss">
.document-submission-list {
  .filters {
    padding: 10px 0 0 0;
    width: 280px;
  }
}
</style>
